<template>
  <div>
    <el-row
      type="flex"
      justify="space-between"
      align="middle"
      class="summary-step__header"
    >
      <span aria-live="polite">{{ countsText }}</span>
      <template v-if="canManageMappings">
        <!-- Edits are lost: the summary is prepared again (C58). -->
        <el-popconfirm
          v-if="isDirty"
          :title="$t(`${I18N}.confirmEditColumns`)"
          :confirm-button-text="$t('global.confirm')"
          :cancel-button-text="$t('global.cancel')"
          @confirm="$emit('editColumns')"
        >
          <el-button
            slot="reference"
            plain
            icon="el-icon-connection"
          >
            {{ $t(`${I18N}.editColumns`) }}
          </el-button>
        </el-popconfirm>
        <el-button
          v-else
          plain
          icon="el-icon-connection"
          @click="$emit('editColumns')"
        >
          {{ $t(`${I18N}.editColumns`) }}
        </el-button>
      </template>
    </el-row>

    <!-- Not an error: said, so an empty or short summary is understood (C59). -->
    <el-alert
      v-if="unmappedColumns.length > 0"
      type="warning"
      :title="$tc(`${I18N}.unmappedColumns.title`, unmappedColumns.length)"
      :closable="false"
      show-icon
      class="summary-step__unmapped"
    >
      <div>{{ unmappedColumns.join(', ') }}</div>
      <div>
        {{
          $tc(
            `${I18N}.unmappedColumns.${
              canManageMappings ? 'fix' : 'fixNoPermission'
            }`,
            unmappedColumns.length
          )
        }}
      </div>
    </el-alert>

    <SummaryRowsTable
      v-loading="isLoadingMeasures"
      :rows="pageRows"
      @value="editValue"
      @ld="editLd"
      @measure="editMeasure"
      @include="include"
      @choose="choose"
    />

    <el-pagination
      v-if="units.length > PAGE_SIZE"
      layout="prev, pager, next"
      :total="units.length"
      :page-size="PAGE_SIZE"
      :current-page="page"
      class="summary-step__pagination"
      @current-change="page = $event"
    />

    <!-- The one action of the step, on the side of the dialog's actions. -->
    <el-row
      type="flex"
      justify="end"
      align="middle"
      class="summary-step__actions"
    >
      <span
        class="summary-step__secondary"
        aria-live="polite"
      >
        {{ counts.blocked > 0 ? $tc(`${I18N}.blocked`, counts.blocked) : '' }}
      </span>
      <el-button
        type="primary"
        :disabled="!canProceed"
        @click="proceed"
      >
        {{ $t(`${I18N}.proceed`) }}
      </el-button>
    </el-row>
  </div>
</template>

<script>
  import debounce from 'lodash.debounce';
  import { analysesApi, measuresApi } from '@/api';
  import SummaryRowsTable, { NOT_IMPORTED } from './SummaryRowsTable.vue';
  import { PAGE_SIZE } from './columnMappings';
  import { COLUMN_STATUSES, serverErrorMessage } from './wizardSteps';
  import {
    ROW_ERRORS,
    choiceChanges,
    conflictsOf,
    createEntry,
    groupKeysOf,
    hasCurrentValue,
    inclusionChanges,
    isDirty,
    isIncludable,
    orderedUnits,
    rowState,
    rowsToWrite,
    summaryCounts,
    written,
  } from './summaryRows';

  // As the value insertion form (FormOperationValue).
  const VALIDATION_DELAY_MS = 800;

  // Step 3 (FE-5). Every row prepare would write, editable: value, LD and unit
  // with REQ-10 applied again, inclusion, the choice among ambiguous
  // candidates. Emits `proceed` with the rows to write, `editColumns` to
  // reopen the columns step and `dirty` while it holds edits (C58).
  export default {
    name: 'SummaryStep',
    components: { SummaryRowsTable },
    props: {
      rows: { type: Array, required: true },
      // `columns` of the same prepare response.
      columns: { type: Array, required: true },
      canManageMappings: { type: Boolean, required: true },
    },
    data() {
      const entries = this.rows.map(createEntry);
      const conflicts = conflictsOf(entries);
      return {
        I18N: 'analysesData.importValues.summary',
        PAGE_SIZE,
        entries,
        conflicts: Object.freeze(conflicts),
        // Once, on open: a row being fixed does not change page.
        units: Object.freeze(orderedUnits(entries, conflicts)),
        page: 1,
        measuresById: {},
        isLoadingMeasures: false,
      };
    },
    computed: {
      // Their values produce no rows.
      unmappedColumns() {
        return this.columns
          .filter(({ status }) => status === COLUMN_STATUSES.UNMAPPED)
          .map(({ sourceText }) => sourceText);
      },
      states() {
        return this.entries.map((entry) =>
          rowState(entry, this.conflicts, this.entries)
        );
      },
      counts() {
        return summaryCounts(this.entries, this.states);
      },
      countsText() {
        const { toWrite, overwrites, errors, nonCompliant } = this.counts;
        const tc = (key, count) =>
          this.$tc(`${this.I18N}.counts.${key}`, count);
        return [
          tc('toWrite', toWrite),
          overwrites > 0 && tc('overwrites', overwrites),
          errors > 0 && tc('errors', errors),
          nonCompliant > 0 && tc('nonCompliant', nonCompliant),
        ]
          .filter(Boolean)
          .join(' · ');
      },
      canProceed() {
        return this.counts.toWrite > 0 && this.counts.blocked === 0;
      },
      isDirty() {
        return this.entries.some(isDirty);
      },
      pageRows() {
        const start = (this.page - 1) * PAGE_SIZE;
        return this.units
          .slice(start, start + PAGE_SIZE)
          .flat()
          .map(this.view);
      },
    },
    watch: {
      isDirty: {
        handler(dirty) {
          this.$emit('dirty', dirty);
        },
        immediate: true,
      },
    },
    created() {
      this.validations = {};
      this.loadMeasures();
    },
    beforeDestroy() {
      Object.values(this.validations).forEach((validate) => validate.cancel());
    },
    methods: {
      view(key) {
        const entry = this.entries[key];
        const groupKeys = groupKeysOf(this.conflicts, key);
        return {
          key,
          row: entry.row,
          valueText: entry.valueText,
          ldText: entry.ldText,
          measureId: entry.measureId,
          measure: this.measuresById[entry.measureId] || null,
          written: written(entry),
          state: this.states[key],
          includable: isIncludable(entry),
          included: entry.included,
          compliant: entry.compliant,
          overwrite: entry.included && hasCurrentValue(entry.row),
          group: groupKeys && this.groupView(key, groupKeys),
        };
      },
      groupView(key, keys) {
        const chosen = keys.find((k) => this.entries[k].included);
        return {
          keys,
          first: key === keys[0],
          size: keys.length,
          choice: chosen === undefined ? NOT_IMPORTED : chosen,
          options: keys.map((k) => ({
            key: k,
            code: this.entries[k].row.analysisCode,
            disabled: !isIncludable(this.entries[k]),
          })),
        };
      },

      // ── Units ─────────────────────────────────────────────────────────

      async loadMeasures() {
        this.isLoadingMeasures = true;
        try {
          const { data } = await measuresApi.index();
          this.measuresById = data.reduce(
            (byId, measure) => ({ ...byId, [measure.id]: measure }),
            {}
          );
        } catch (error) {
          this.notifyError(serverErrorMessage(error));
        } finally {
          this.isLoadingMeasures = false;
        }
      },

      // ── Edits ─────────────────────────────────────────────────────────

      editValue({ key, text }) {
        const entry = this.entries[key];
        const wasReadable = written(entry).value !== null;
        entry.valueText = text;
        // A value that could not be read was its only reason to be left out.
        if (
          entry.row.errorCode === ROW_ERRORS.VALUE_NOT_NUMERIC &&
          !entry.included &&
          !wasReadable &&
          written(entry).value !== null
        ) {
          this.include({ key, included: true });
        }
        this.checkCompliance(key);
      },
      editLd({ key, text }) {
        this.entries[key].ldText = text;
        this.checkCompliance(key);
      },
      editMeasure({ key, measureId, measure }) {
        this.entries[key].measureId = measureId;
        if (measure) this.$set(this.measuresById, measureId, measure);
      },
      include({ key, included }) {
        this.apply(inclusionChanges(this.conflicts, key, included));
      },
      choose({ keys, key }) {
        this.apply(
          choiceChanges(this.conflicts, keys, key === NOT_IMPORTED ? null : key)
        );
      },
      apply(changes) {
        Object.entries(changes).forEach(([key, included]) => {
          this.entries[key].included = included;
        });
      },

      // ── Compliance (REQ-16) ───────────────────────────────────────────

      // Only the written value decides compliance (ValueValidationService).
      checkCompliance(key) {
        const entry = this.entries[key];
        const { value } = written(entry);
        if (value === entry.checkedValue) return;
        entry.checkedValue = value;
        entry.compliant = null;
        if (value === null) return;
        this.validationOf(key)(key, value);
      },
      validationOf(key) {
        if (!this.validations[key]) {
          this.validations[key] = debounce(this.validate, VALIDATION_DELAY_MS);
        }
        return this.validations[key];
      },
      async validate(key, value) {
        const entry = this.entries[key];
        const { row } = entry;
        try {
          const { data } = await analysesApi.validateOperation({
            id: row.operationValueId,
            labSampleAnalysisId: row.labSampleAnalysisId,
            operationRegistryId: row.operationRegistryId,
            value,
          });
          // An answer for a value since replaced is dropped.
          if (entry.checkedValue === value) entry.compliant = data.isValid;
        } catch (error) {
          this.notifyError(serverErrorMessage(error));
        }
      },

      proceed() {
        if (!this.canProceed) return;
        this.$emit('proceed', rowsToWrite(this.entries));
      },
      notifyError(message) {
        this.$notify({ showClose: true, type: 'error', message });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .summary-step__header {
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .summary-step__unmapped {
    margin-bottom: 1rem;
  }

  .summary-step__pagination {
    margin-top: 1rem;
    text-align: right;
  }

  .summary-step__actions {
    gap: 1rem;
    margin-top: 1rem;
  }

  .summary-step__secondary {
    color: $--color-text-regular;
  }
</style>
